import React from "react";
import styled from "styled-components";
import { IAffiliatePayment, PaymentHistoryViewState } from "./types";
import { affiliateError } from "./fees";
import { Card, Notice, Table } from "./style";

function ChevronThick() {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M7.5 15L12.5 10L7.5 5"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function utcDate(value?: string | null): string {
  if (!value) return "—";
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return "—";
  const date = new Date(time);
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][date.getUTCMonth()];
  return `${String(date.getUTCDate()).padStart(2, "0")} ${month} ${String(
    date.getUTCFullYear()
  ).slice(-2)}`;
}

const PAYMENT_LABELS = {
  paid: "Paid",
  submitted: "Submitted",
  reserved: "Reserved",
};
const PAYMENT_COLORS = {
  paid: "#00E100",
  submitted: "#4CD7F4",
  reserved: "#FF7A00",
};
function paymentStatus(payment: IAffiliatePayment) {
  return PAYMENT_LABELS[payment.status];
}

const PaymentCard = styled(Card)`
  margin: 0;
  padding: 24px 0;
  h2 {
    font-size: 22px;
    line-height: 28px;
    font-weight: 300;
    margin: 0 0 16px;
  }
  p {
    margin: 8px 0 16px;
    font-size: 14px;
    line-height: 20px;
  }
  .mobile-copy {
    display: none;
  }
  @media (max-width: 760px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px 0 0;
    border-bottom: 0;
    h2 {
      font-size: 20px;
      line-height: 24px;
      margin: 0;
    }
    p {
      margin: 0;
      font-size: 12px;
      line-height: 16px;
    }
    .desktop-copy {
      display: none;
    }
    .mobile-copy {
      display: inline;
    }
  }
`;

const DesktopPayments = styled.div`
  overflow-x: auto;
  @media (max-width: 760px) {
    display: none;
  }
`;

const PaymentsTable = styled(Table)`
  && {
    font-size: 12px;
    line-height: 16px;
    color: #a5a5a8;
    font-variant-numeric: tabular-nums;
    th,
    td {
      height: 40px;
      padding: 0 8px;
      white-space: nowrap;
    }
    th {
      height: 32px;
      color: #6a6a6d;
      font-weight: 400;
    }
    th:first-child,
    td:first-child {
      padding-left: 0;
      text-align: left;
    }
    td:first-child {
      padding-top: 8px;
      padding-bottom: 8px;
    }
    th:not(:first-child),
    td:not(:first-child) {
      text-align: right;
    }
    th:last-child,
    td:last-child {
      padding-right: 0;
    }
    td:first-child strong {
      display: block;
      color: #fff;
      font-weight: 400;
    }
    td:first-child small {
      display: block;
      margin-top: 2px;
      color: #6a6a6d;
      font-size: 11px;
    }
    td.amount {
      color: #fff;
    }
    tbody tr {
      transition: background 200ms ease-in-out;
    }
  }
`;

const StatusPill = styled.span<{ $status: IAffiliatePayment["status"] }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ $status }) => PAYMENT_COLORS[$status]};
  &::before {
    content: "";
    width: 5px;
    height: 5px;
    flex: 0 0 5px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 6px currentColor;
  }
`;

const MobilePayments = styled.div`
  display: none;
  @media (max-width: 760px) {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0 16px;
    border: 1px solid #1a1a1f;
    border-radius: 12px;
    background: #12121a;
    min-width: 0;
    article {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      min-width: 0;
      min-height: 60px;
      border-bottom: 1px solid #1a1a1f;
    }
    article:last-child {
      border-bottom: 0;
    }
    article > div {
      min-width: 0;
    }
    .amount,
    .payout-id {
      display: block;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .amount {
      color: #fff;
      font-size: 15px;
      line-height: 20px;
      font-weight: 400;
      font-variant-numeric: tabular-nums;
    }
    .payout-id {
      color: #6a6a6d;
      font-size: 11px;
      line-height: 12px;
      margin-top: 4px;
    }
    .state {
      flex-shrink: 0;
      text-align: right;
    }
    .state small {
      display: block;
      color: #6a6a6d;
      font-size: 11px;
      line-height: 12px;
      margin-top: 6px;
    }
  }
`;

const Pager = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
  min-height: 64px;
  padding: 12px 0;
  border-top: 1px solid #15151b;
  color: #a5a5a8;
  font-size: 12px;
  > span strong {
    color: #fff;
    font-weight: 400;
  }
  .controls {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .page {
    color: #fff;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }
  .rule {
    width: 64px;
    height: 2px;
    background: #4cd7f4;
  }
  .last-page {
    color: #6a6a6d;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }
  .mobile-count {
    display: none;
  }
  .spacer {
    flex: 1;
  }
  @media (max-width: 760px) {
    min-height: 0;
    padding: 4px 0 0;
    border: 0;
    gap: 0;
    justify-content: center;
    .desktop-count,
    .spacer {
      display: none;
    }
    .controls {
      position: static;
      transform: none;
      justify-content: center;
      gap: 16px;
      width: 100%;
    }
    .rule {
      width: 48px;
    }
    .mobile-count {
      display: block;
      width: 100%;
      text-align: center;
      color: #6a6a6d;
      font-size: 11px;
      line-height: 12px;
      margin-top: 8px;
    }
  }
`;

const PagerButton = styled.button<{ $previous?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: #18181e;
  color: #fff;
  cursor: pointer;
  svg {
    width: 20px;
    height: 20px;
    transform: ${({ $previous }) => ($previous ? "rotate(180deg)" : "none")};
  }
  &:hover:not(:disabled) {
    box-shadow: inset 0 0 0 1px #fff;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid #4cd7f4;
    outline-offset: 2px;
  }
  @media (max-width: 760px) {
    width: 44px;
    height: 44px;
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

function CreatedDate({ value }: { value: string | null }) {
  return value ? (
    <>{utcDate(value)}</>
  ) : (
    <>
      <span aria-hidden="true">—</span>
      <VisuallyHidden>Not recorded</VisuallyHidden>
    </>
  );
}

function PaidDate({ payment }: { payment: IAffiliatePayment }) {
  return payment.status === "paid" ? (
    <>{utcDate(payment.paid_at)}</>
  ) : (
    <>
      <span aria-hidden="true">—</span>
      <VisuallyHidden>Not paid</VisuallyHidden>
    </>
  );
}

export function PaymentHistoryView({
  history,
}: {
  history: PaymentHistoryViewState;
}) {
  const payments = history.data?.payments || [];
  const start = payments.length ? (history.page - 1) * 50 + 1 : 0;
  const end = payments.length ? start + payments.length - 1 : 0;
  const totalCount =
    history.totalCount ??
    (history.page === 1 && !history.data?.next_cursor
      ? payments.length
      : undefined);
  let totalPages: number | undefined;
  if (totalCount !== undefined)
    totalPages = Math.max(1, Math.ceil(totalCount / 50));
  else if (history.data && !history.data.next_cursor) totalPages = history.page;
  const shownRange = start ? `${start}–${end}` : "0";
  const desktopShownRange = start ? `${start}-${end}` : "0";
  return (
    <PaymentCard>
      <h2>Payouts</h2>
      <p>
        <span className="desktop-copy">
          Only “Paid” confirms cash reached your Aevo balance. Reserved and
          submitted payments are still in progress.
        </span>
        <span className="mobile-copy">
          Only “Paid” confirms cash reached your Aevo balance.
        </span>
      </p>
      {Boolean(history.error) && (
        <Notice $error role="alert">
          {affiliateError(history.error)}{" "}
          <button type="button" onClick={() => history.refresh()}>
            Retry payments
          </button>
        </Notice>
      )}
      {!history.error && !history.data && (
        <p role="status">Loading payments…</p>
      )}
      {!history.error && history.data && payments.length > 0 && (
        <>
          <DesktopPayments>
            <PaymentsTable>
              <thead>
                <tr>
                  <th>Payout / transfer</th>
                  <th>USDC</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Paid</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={`${payment.payout_id}:${payment.transfer_id}`}>
                    <td>
                      <strong>{payment.payout_id}</strong>
                      <small>{payment.transfer_id || "Transfer pending"}</small>
                    </td>
                    <td className="amount">${payment.amount}</td>
                    <td>
                      <StatusPill $status={payment.status}>
                        {paymentStatus(payment)}
                      </StatusPill>
                    </td>
                    <td>
                      <CreatedDate value={payment.created_at} />
                    </td>
                    <td>
                      <PaidDate payment={payment} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </PaymentsTable>
          </DesktopPayments>
          <MobilePayments>
            {payments.map((payment) => (
              <article
                key={`mobile:${payment.payout_id}:${payment.transfer_id}`}
              >
                <div>
                  <strong className="amount">
                    <span>$</span>
                    <span>{payment.amount}</span>
                  </strong>
                  <small className="payout-id" title={payment.payout_id}>
                    {payment.payout_id}
                  </small>
                  <VisuallyHidden>
                    {payment.transfer_id || "Transfer pending"}
                  </VisuallyHidden>
                </div>
                <div className="state">
                  <StatusPill $status={payment.status}>
                    {paymentStatus(payment)}
                  </StatusPill>
                  <small>
                    {utcDate(
                      payment.status === "paid"
                        ? payment.paid_at
                        : payment.created_at
                    )}
                  </small>
                </div>
              </article>
            ))}
          </MobilePayments>
        </>
      )}
      {!history.error && history.data && payments.length === 0 && (
        <p>No cash payments have been recorded.</p>
      )}
      <Pager>
        <span className="desktop-count">
          Showing <strong>{desktopShownRange}</strong>
          {totalCount === undefined ? (
            " items"
          ) : (
            <>
              {" out of "}
              <strong>{totalCount}</strong> items
            </>
          )}
        </span>
        <div className="controls">
          <PagerButton
            $previous
            type="button"
            aria-label="Previous page"
            disabled={history.isValidating || history.page === 1}
            onClick={history.previous}
          >
            <ChevronThick />
          </PagerButton>
          <span className="page">{String(history.page).padStart(2, "0")}</span>
          <span className="rule" aria-hidden="true" />
          <span className="last-page">
            {totalPages === undefined
              ? "…"
              : String(totalPages).padStart(2, "0")}
          </span>
          <PagerButton
            type="button"
            aria-label="Next page"
            disabled={history.isValidating || !history.data?.next_cursor}
            onClick={history.next}
          >
            <ChevronThick />
          </PagerButton>
        </div>
        <span className="mobile-count">
          Showing {shownRange}
          {totalCount === undefined ? " items" : ` of ${totalCount}`}
        </span>
        <span className="spacer" aria-hidden="true" />
      </Pager>
    </PaymentCard>
  );
}
